import * as React from "react"
import Link from "next/link"
import { SafeImage } from "@/components/ui/safe-image"
import { SectionHeader } from "./section-header"
import type { RelatedBrand } from "./types"

interface MoreBrandsStripProps {
  brands: RelatedBrand[]
}

/** Initials fallback when a related brand has no logo. */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

/** Sub-brand / corporate-variant slug patterns to skip when picking related
 *  brands. The DB has a long tail of duplicated brand rows (`ge-appliances`,
 *  `geappliances`, `lg-electronics`, `frigidaire-gallery`, `summit` vs
 *  `summit-appliance`, etc.). Their logo files are usually low-resolution
 *  variants of the parent brand mark, so they render tiny inside the
 *  84px tile and make the row feel inconsistent. We drop them here and let
 *  the canonical parent slug (`ge`, `lg`, `frigidaire`, …) carry the
 *  representation instead. */
const SUBBRAND_SUFFIXES = [
  /-appliances?$/i,
  /-electronics$/i,
  /-gallery$/i,
  /-profile$/i,
  /-corporation$/i,
  /-corp$/i,
  /-refrigeration$/i,
  /-refrigerator$/i,
]
/** Explicit sub-brand slugs that don't match the suffix patterns above.
 *  Add to this list when a new noisy duplicate surfaces. */
const SUBBRAND_SLUGS_DENYLIST = new Set([
  "geappliances", // no-dash variant of ge-appliances
])

function isSubBrand(slug: string): boolean {
  if (SUBBRAND_SLUGS_DENYLIST.has(slug)) return true
  return SUBBRAND_SUFFIXES.some(re => re.test(slug))
}

/**
 * "More Brands on Fridge.com" — 6 logo tiles in a single row.
 *
 * The caller passes the relevant-brand pool; this component dedupes
 * sub-brand / corporate-variant slugs (so the user doesn't see both `ge`
 * and `ge-appliances` in the same strip) before slicing to 6. Logos are
 * sized to fill ~70% of the tile's short axis so brands with bold marks
 * fill the cell properly; we trade a little white-space for legibility.
 *
 * Tile sizing: `h-[112px]` with `p-3` padding leaves ~88px of inner room.
 * `max-h-[78px]` clamps the rendered logo to 78px tall — a 30% bump over
 * the previous 60px cap so brand marks feel substantially zoomed-in.
 * `max-w-[92%]` lets wide wordmarks (Frigidaire, KitchenAid) extend
 * almost edge-to-edge while still leaving a hairline of margin.
 */
export function MoreBrandsStrip({ brands }: MoreBrandsStripProps) {
  const filtered = brands.filter(b => b.logo_path && !isSubBrand(b.slug)).slice(0, 6)
  if (filtered.length === 0) return null
  return (
    <section className="my-12">
      <SectionHeader
        eyebrow="Related brands"
        title="More Brands on Fridge.com"
        action={{ type: "link", label: "All brands", href: "/brands" }}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {filtered.map(b => (
          <Link
            key={b.slug}
            href={`/brands/${b.slug}`}
            prefetch={false}
            title={b.name}
            aria-label={b.name}
            className="group grid h-[112px] place-items-center rounded-[12px] border border-gray-200 bg-white p-3 transition-all duration-200 hover:-translate-y-[3px] hover:border-[#ff9951] hover:shadow-[0_14px_28px_-16px_rgba(0,0,0,0.18)]"
          >
            {b.logo_path ? (
              <SafeImage
                src={b.logo_path}
                alt={`${b.name} logo`}
                width={300}
                height={156}
                sizes="(max-width: 480px) 45vw, (max-width: 900px) 30vw, 16vw"
                // FRIDGE-1540: the old caps are the box, so the lazy logo has a size before it
                // loads. The file's drawn size comes from `sizes`, so it can fit the box at one
                // width and not at another; scale-down draws it at that size when it fits and
                // shrinks it when it does not, centered, exactly as the caps did.
                style={{ width: '92%', height: 78, objectFit: 'scale-down' }}
              />
            ) : (
              <div className="grid h-[72px] w-[72px] place-items-center rounded-[10px] bg-gray-50">
                <span className="text-[28px] font-black text-gray-700">{deriveInitials(b.name)}</span>
              </div>
            )}
          </Link>
        ))}
      </div>
    </section>
  )
}
